import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { GraduationCap, Globe, MessageSquare, FileCheck, CheckCircle, ArrowRight } from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen">
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-md z-50 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_edujourney-29/artifacts/h5igihub_britsfly.png" 
              alt="BritsFly Logo" 
              className="h-10 w-auto"
            />
            <span className="font-heading font-bold text-2xl text-foreground">BritsFly</span>
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="nav-login-btn"
              variant="ghost"
              onClick={() => navigate('/login')}
              className="rounded-full"
            >
              Login
            </Button>
            <Button
              data-testid="nav-register-btn"
              onClick={() => navigate('/register')}
              className="rounded-full bg-primary hover:bg-primary/90 text-white font-medium px-6"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      <section className="pt-32 pb-20 px-6 hero-gradient">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <h1 className="font-heading font-bold text-5xl md:text-6xl tracking-tight text-foreground leading-tight">
              Your Dream University
              <span className="block text-primary mt-2">Journey Starts Here</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Track your study abroad application in real-time. From consultation to visa approval, we guide you every step of the way to UK, USA, Ireland, Europe & Australia.
            </p>
            <div className="flex gap-4">
              <Button
                data-testid="hero-get-started-btn"
                onClick={() => navigate('/register')}
                size="lg"
                className="rounded-full bg-primary hover:bg-primary/90 text-white font-medium px-8 py-6 text-base"
              >
                Start Your Journey <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                data-testid="hero-login-btn"
                onClick={() => navigate('/login')}
                variant="outline"
                size="lg"
                className="rounded-full font-medium px-8 py-6 text-base"
              >
                Login
              </Button>
            </div>
          </div>
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1758270705087-76e81a5117bd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwdW5pdmVyc2l0eSUyMHN0dWRlbnRzJTIwY2FtcHVzJTIwbGF1Z2hpbmd8ZW58MHx8fHwxNzY2MzQxOTU2fDA&ixlib=rb-4.1.0&q=85"
              alt="Diverse students on campus"
              className="rounded-3xl shadow-2xl w-full"
            />
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading font-semibold text-4xl md:text-5xl text-foreground mb-4">
              Track Every Step
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Our comprehensive 10-stage process ensures nothing falls through the cracks
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: MessageSquare, title: 'Consultation', desc: 'Expert guidance on university selection' },
              { icon: FileCheck, title: 'Document Submission', desc: 'Upload and track all required documents' },
              { icon: GraduationCap, title: 'University Selection', desc: 'Choose from top global institutions' },
              { icon: CheckCircle, title: 'Application Tracking', desc: 'Real-time updates on your progress' },
              { icon: Globe, title: 'Visa Processing', desc: 'Complete visa assistance and guidance' },
              { icon: CheckCircle, title: 'Final Approval', desc: 'Get ready for your new adventure' }
            ].map((feature, idx) => (
              <div
                key={idx}
                className="p-8 rounded-2xl bg-muted hover:bg-white hover:shadow-lg border border-transparent hover:border-primary/20 transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <feature.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-xl text-foreground mb-2">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-6 bg-gradient-to-br from-primary/5 via-white to-accent/5">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h2 className="font-heading font-semibold text-4xl md:text-5xl text-foreground">
            Ready to Begin Your Journey?
          </h2>
          <p className="text-lg text-muted-foreground">
            Join thousands of students who are already on their path to studying abroad
          </p>
          <Button
            data-testid="cta-register-btn"
            onClick={() => navigate('/register')}
            size="lg"
            className="rounded-full bg-primary hover:bg-primary/90 text-white font-medium px-10 py-6 text-lg"
          >
            Create Your Free Account
          </Button>
        </div>
      </section>

      <footer className="py-12 px-6 bg-foreground text-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_edujourney-29/artifacts/h5igihub_britsfly.png" 
              alt="BritsFly Logo" 
              className="h-8 w-auto brightness-0 invert"
            />
            <span className="font-heading font-bold text-xl">BritsFly</span>
          </div>
          <p className="text-white/70">
            Your trusted partner for international education
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;